import React, { useState, useRef, useCallback } from 'react';
import { X, Plus, Upload, Download, FileText, AlertCircle, Trash2 } from 'lucide-react';
import { BulkVinItem, parseVinsFromCsv, downloadCsvTemplate } from '../services/vehicleReportService';
import { getErrorMessage } from '../services/apiService';

interface BulkVINInputProps {
  onSubmit: (items: BulkVinItem[]) => void;
  disabled?: boolean;
  maxVins?: number;
  costPerVin?: number | null;
}

const BulkVINInput: React.FC<BulkVINInputProps> = ({
  onSubmit,
  disabled = false,
  maxVins = 50,
  costPerVin = null,
}) => {
  const [inputMode, setInputMode] = useState<'manual' | 'csv'>('manual');
  const [manualItems, setManualItems] = useState<BulkVinItem[]>([{ vin: '', referenceNote: '' }]);
  const [csvItems, setCsvItems] = useState<BulkVinItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate VIN format (basic check)
  const isValidVin = (vin: string): boolean => {
    if (!vin) return false;
    const cleaned = vin.toUpperCase().trim();
    // Check length and invalid characters
    if (cleaned.length < 5 || cleaned.length > 17) return false;
    if (/[IOQ]/.test(cleaned)) return false;
    return true;
  };

  // Get current items based on input mode
  const getCurrentItems = useCallback((): BulkVinItem[] => {
    if (inputMode === 'manual') {
      return manualItems
        .filter(item => item.vin.trim())
        .map(item => ({
          vin: item.vin.toUpperCase().trim(),
          referenceNote: item.referenceNote?.trim() || undefined,
        }));
    }
    return csvItems;
  }, [inputMode, manualItems, csvItems]);

  // Get valid items count
  const validItemsCount = getCurrentItems().filter(item => isValidVin(item.vin)).length;

  // Handle manual item change
  const handleManualItemChange = (index: number, field: 'vin' | 'referenceNote', value: string) => {
    const newItems = [...manualItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setManualItems(newItems);
    setError('');
  };

  // Add new manual item row
  const addManualRow = () => {
    if (manualItems.length >= maxVins) {
      setError(`Maximum ${maxVins} VINs allowed`);
      return;
    }
    setManualItems([...manualItems, { vin: '', referenceNote: '' }]);
  };

  // Remove manual item row
  const removeManualRow = (index: number) => {
    if (manualItems.length === 1) {
      setManualItems([{ vin: '', referenceNote: '' }]);
    } else {
      setManualItems(manualItems.filter((_, i) => i !== index));
    }
    setError('');
  };

  // Process uploaded file
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;

      try {
        const items = parseVinsFromCsv(content);

        if (items.length === 0) {
          setError('No valid VINs found in the file');
          return;
        }

        if (items.length > maxVins) {
          setError(`File contains ${items.length} VINs. Maximum ${maxVins} allowed. Only first ${maxVins} will be used.`);
          setCsvItems(items.slice(0, maxVins));
        } else {
          setError('');
          setCsvItems(items);
        }
      } catch (err) {
        setError(getErrorMessage(err));
        setCsvItems([]);
      }
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  }, [maxVins]);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }, [processFile]);

  // Handle file select
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Clear all items
  const handleClear = () => {
    if (inputMode === 'manual') {
      setManualItems([{ vin: '', referenceNote: '' }]);
    } else {
      setCsvItems([]);
    }
    setError('');
  };

  // Submit items
  const handleSubmit = () => {
    const items = getCurrentItems();
    const validItems = items.filter(item => isValidVin(item.vin));

    if (validItems.length === 0) {
      setError('Please enter at least one valid VIN');
      return;
    }

    // Check for duplicates
    const seen = new Set<string>();
    const duplicates: string[] = [];
    validItems.forEach(item => {
      if (seen.has(item.vin)) {
        duplicates.push(item.vin);
      } else {
        seen.add(item.vin);
      }
    });

    if (duplicates.length > 0) {
      setError(`Duplicate VINs detected and will be removed: ${duplicates.join(', ')}`);
    }

    // Remove duplicates and submit
    const uniqueItems = validItems.filter((item, index, self) =>
      index === self.findIndex(i => i.vin === item.vin)
    );

    onSubmit(uniqueItems);
  };

  return (
    <div className="space-y-4">
      {/* Mode Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setInputMode('manual')}
          disabled={disabled}
          className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${
            inputMode === 'manual'
              ? 'bg-[#c41e3a]/10 text-[#c41e3a] border border-[#c41e3a]/30'
              : 'bg-white/60 text-[#64748b] border border-black/[0.08] hover:bg-white hover:text-[#0f172a]'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <FileText className="w-4 h-4" />
          Manual Entry
        </button>
        <button
          onClick={() => setInputMode('csv')}
          disabled={disabled}
          className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${
            inputMode === 'csv'
              ? 'bg-[#c41e3a]/10 text-[#c41e3a] border border-[#c41e3a]/30'
              : 'bg-white/60 text-[#64748b] border border-black/[0.08] hover:bg-white hover:text-[#0f172a]'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <Upload className="w-4 h-4" />
          CSV Upload
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-lg text-[#dc2626] text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Manual Entry Mode */}
      {inputMode === 'manual' && (
        <div className="space-y-3">
          <div className="text-xs text-[#64748b]">
            Enter VINs with optional reference notes. Maximum {maxVins} VINs.
          </div>

          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 text-xs font-medium text-[#64748b] px-4">
            <div className="col-span-5">VIN</div>
            <div className="col-span-6">Reference Note (Optional)</div>
            <div className="col-span-1"></div>
          </div>

          {/* Input rows */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto -mx-4 px-4 py-1">
            {manualItems.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  value={item.vin}
                  onChange={(e) => handleManualItemChange(index, 'vin', e.target.value.toUpperCase())}
                  disabled={disabled}
                  placeholder="Enter VIN..."
                  className={`col-span-5 px-3 py-2 bg-white border rounded-lg text-[#0f172a] text-sm font-mono placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] focus:border-transparent transition-all disabled:opacity-50 ${
                    item.vin && !isValidVin(item.vin)
                      ? 'border-[#dc2626]/50'
                      : 'border-black/[0.08]'
                  }`}
                  maxLength={17}
                />
                <input
                  type="text"
                  value={item.referenceNote || ''}
                  onChange={(e) => handleManualItemChange(index, 'referenceNote', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g., Customer name, Invoice #..."
                  className="col-span-6 px-3 py-2 bg-white border border-black/[0.08] rounded-lg text-[#0f172a] text-sm placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] focus:border-transparent transition-all disabled:opacity-50"
                />
                <button
                  onClick={() => removeManualRow(index)}
                  disabled={disabled}
                  className="col-span-1 p-2 text-[#94a3b8] hover:text-[#dc2626] transition-colors disabled:opacity-50"
                  title="Remove row"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Row Button */}
          <button
            onClick={addManualRow}
            disabled={disabled || manualItems.length >= maxVins}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[#64748b] hover:text-[#c41e3a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add Row
          </button>
        </div>
      )}

      {/* CSV Upload Mode */}
      {inputMode === 'csv' && (
        <div className="space-y-4">
          {/* Download Template Link */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#64748b]">
              Upload a CSV file with VIN and ReferenceNote columns.
            </span>
            <button
              onClick={downloadCsvTemplate}
              disabled={disabled}
              className="flex items-center gap-1 text-xs text-[#c41e3a] hover:text-[#a01830] font-medium transition-colors disabled:opacity-50"
            >
              <Download className="w-3 h-3" />
              Download Template
            </button>
          </div>

          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onClick={() => !disabled && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              dragActive
                ? 'border-[#c41e3a] bg-[#c41e3a]/5'
                : 'border-black/[0.15] hover:border-[#c41e3a]/50 hover:bg-white/60'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Upload className="w-10 h-10 mx-auto mb-3 text-[#94a3b8]" />
            <p className="text-sm text-[#0f172a] mb-1">
              Drag & drop your CSV file here
            </p>
            <p className="text-xs text-[#64748b]">
              or click to browse
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileSelect}
            disabled={disabled}
            className="hidden"
          />

          {/* CSV Preview */}
          {csvItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#0f172a]">
                  {csvItems.length} VIN(s) loaded
                </span>
                <button
                  onClick={() => setCsvItems([])}
                  disabled={disabled}
                  className="flex items-center gap-1 text-xs text-[#64748b] hover:text-[#dc2626] transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear
                </button>
              </div>

              <div className="max-h-[200px] overflow-y-auto bg-white rounded-lg border border-black/[0.08]">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8fafc] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">VIN</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">Reference Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvItems.map((item, index) => (
                      <tr key={index} className="border-t border-black/[0.05]">
                        <td className="px-3 py-2 text-[#64748b]">{index + 1}</td>
                        <td className={`px-3 py-2 font-mono text-xs ${
                          isValidVin(item.vin) ? 'text-[#0f172a]' : 'text-[#dc2626]'
                        }`}>
                          {item.vin}
                        </td>
                        <td className="px-3 py-2 text-[#64748b]">{item.referenceNote || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-black/[0.06]">
        <div className="flex items-center gap-3">
          <div className="text-sm text-[#64748b]">
            {validItemsCount > 0 ? (
              <span className="text-[#16a34a] font-medium">{validItemsCount} valid VIN(s) ready</span>
            ) : (
              <span>No VINs entered</span>
            )}
          </div>
          {costPerVin !== null && costPerVin > 0 && validItemsCount > 0 && (
            <span className="badge-success px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap">
              Est. ${(costPerVin * validItemsCount).toFixed(2)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClear}
            disabled={disabled}
            className="px-4 py-2 text-sm text-[#64748b] hover:text-[#0f172a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear
          </button>
          <button
            onClick={handleSubmit}
            disabled={disabled || validItemsCount === 0}
            className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Search {validItemsCount > 0 ? `(${validItemsCount})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkVINInput;
